// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import {decrementIssuesTabCounter, incrementIssuesTabCounter} from '@github-ui/issues-tab-counter'
import {adjustOpenClosedCounts} from '@github-ui/issues-tab-counter/open-closed-count'
import {
  type DeltaEntityFieldValuesOperation,
  DOCUMENT_FIELDS_PATH,
  type SetFieldsOperation,
} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER, entityFieldsMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import {useMutation} from '@github-ui/react-query'
import {setIssueStatusMutation as setIssueStatusQueryId} from '@github-ui/server-persisted-queries'
import type {SetIssueStatusMutationMutation} from '@github-ui/server-persisted-queries/types/setIssueStatusMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useRef} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate, type RecordSourceProxy} from 'relay-runtime'

type IssueState = 'OPEN' | 'CLOSED'

export type SetIssueStatusDuplicate = {
  id: string
  number: number
  url: string
  repository: {name: string; owner: {login: string}}
}

type ConfirmedIssue = NonNullable<NonNullable<SetIssueStatusMutationMutation['setIssueStatus']>['issue']>
type ConfirmedParent = NonNullable<ConfirmedIssue['parent']>

export type IssueStatusOption = NonNullable<ConfirmedIssue['status']>

/** A status whose category closes the issue as a duplicate needs a canonical issue to point at. */
export function needsDuplicateIssue(status: IssueStatusOption): boolean {
  return status.category.stateReason === 'DUPLICATE'
}

export type SetIssueStatusInput = {
  status: IssueStatusOption
  currentState: IssueState
  currentStateReason: string | null
  duplicateIssue?: SetIssueStatusDuplicate
}

export type SetIssueStatusCallbacks = {
  onCompleted?: (issue: ConfirmedIssue) => void
  onError?: (error: Error) => void
}

export type SetIssueStatus = (input: SetIssueStatusInput, callbacks?: SetIssueStatusCallbacks) => void

const SET_STATUS_ERROR = 'Failed to update the issue status'

type IssueStatusFields = {
  state: IssueState
  stateReason: string | null
  closedAt?: string | null
  duplicateOf: SetIssueStatusDuplicate | ConfirmedIssue['duplicateOf'] | null
  status: IssueStatusOption | null
}

// The status's category decides the state and reason. An open status keeps the current reason on an
// open issue and reopens a closed one, the same as Issues.domain.statuses.set on the server.
function optimisticFields(input: SetIssueStatusInput): IssueStatusFields {
  const {status} = input
  if (status.category.state === 'OPEN') {
    const reopening = input.currentState === 'CLOSED'
    return {
      state: 'OPEN' as const,
      stateReason: reopening ? 'REOPENED' : input.currentStateReason,
      closedAt: null,
      duplicateOf: null,
      status,
    }
  }
  return {
    state: 'CLOSED' as const,
    stateReason: status.category.stateReason,
    // The server preserves the original close time when only the closed status changes.
    ...(input.currentState === 'OPEN' ? {closedAt: new Date().toISOString()} : {}),
    duplicateOf: needsDuplicateIssue(status) ? (input.duplicateIssue ?? null) : null,
    status,
  }
}

function toOperation(issueId: string, fields: IssueStatusFields): SetFieldsOperation {
  return {
    type: 'setFields',
    id: issueId,
    fields: {...fields, closed: fields.state === 'CLOSED'},
  }
}

function parentOperation(parentIssueId: string, completedDelta: number): DeltaEntityFieldValuesOperation {
  return {
    type: 'deltaEntityFieldValues',
    id: parentIssueId,
    field: 'subIssuesSummary',
    deltas: {completed: completedDelta},
  }
}

// RELAY-CLEANUP(HeaderStatus): mirror the new state into Relay until IssueActions and the retained
// issue-list counts stop reading it.
function writeStateToRelay(
  store: RecordSourceProxy,
  issueId: string,
  fields: {
    state: IssueState
    stateReason: string | null
    closedAt?: string | null
    duplicateOf: {id: string; number: number; url: string} | null
  },
) {
  const record = store.get(issueId)
  if (!record) return
  record.setValue(fields.state, 'state')
  record.setValue(fields.stateReason, 'stateReason')
  if (fields.closedAt !== undefined) record.setValue(fields.closedAt, 'closedAt')
  if (fields.duplicateOf) {
    const duplicate = store.get(fields.duplicateOf.id) ?? store.create(fields.duplicateOf.id, 'Issue')
    duplicate.setValue(fields.duplicateOf.number, 'number')
    duplicate.setValue(fields.duplicateOf.url, 'url')
    record.setLinkedRecord(duplicate, 'duplicateOf')
  } else {
    record.setValue(null, 'duplicateOf')
  }
}

function adjustParentSummaryInRelay(store: RecordSourceProxy, parentIssueId: string, completedDelta: number): void {
  const summary = store.get(parentIssueId)?.getLinkedRecord('subIssuesSummary')
  if (!summary) return
  const total = summary.getValue('total')
  const completed = summary.getValue('completed')
  if (typeof total !== 'number' || typeof completed !== 'number') return
  summary.setValue(Math.max(0, Math.min(total, completed + completedDelta)), 'completed')
}

function writeParentSummaryToRelay(store: RecordSourceProxy, parent: ConfirmedParent): void {
  const parentRecord = store.get(parent.id) ?? store.create(parent.id, 'Issue')
  const summary =
    parentRecord.getLinkedRecord('subIssuesSummary') ??
    store.create(`${parent.id}:subIssuesSummary:set-status`, 'SubIssuesSummary')
  summary.setValue(parent.subIssuesSummary.total, 'total')
  summary.setValue(parent.subIssuesSummary.completed, 'completed')
  parentRecord.setLinkedRecord(summary, 'subIssuesSummary')
}

async function setIssueStatusRequest({
  owner,
  repo,
  issueNumber,
  issueId,
  statusId,
  duplicateIssueId,
}: {
  owner: string
  repo: string
  issueNumber: number
  issueId: string
  statusId: string
  duplicateIssueId?: string
}): Promise<ConfirmedIssue> {
  const query = new URLSearchParams({queryId: setIssueStatusQueryId})
  const response = await reactFetchJSON(
    `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${issueNumber}/state.json?${query}`,
    {method: 'PATCH', body: {statusId, duplicateIssueId}},
  )

  let json: {data?: SetIssueStatusMutationMutation | null; errors?: ReadonlyArray<{message?: string}>; error?: string}
  try {
    json = await response.json()
  } catch {
    throw new Error(SET_STATUS_ERROR)
  }

  const issue = json.data?.setIssueStatus?.issue
  if (!response.ok || !issue || issue.id !== issueId || issue.status?.id !== statusId) {
    throw new Error(json.errors?.find(item => item.message)?.message ?? json.error ?? SET_STATUS_ERROR)
  }
  return issue
}

function useParentSummaryMutation() {
  return useMutation<
    ConfirmedParent,
    Error,
    {
      operation: DeltaEntityFieldValuesOperation
      parentIssueId: string
      result: Promise<ConfirmedIssue>
    }
  >({
    mutationKey: entityFieldsMutationKey(),
    mutationFn: async ({parentIssueId, result}) => {
      const parent = (await result).parent
      if (!parent || parent.id !== parentIssueId) {
        throw new Error('Parent sub-issue summary was missing from the response')
      }
      return parent
    },
    onSuccess: parent =>
      notifySettlement(
        {type: 'setFields', id: parent.id, fields: {subIssuesSummary: parent.subIssuesSummary}},
        ENTITY_FIELDS_OWNER,
        DOCUMENT_FIELDS_PATH,
      ),
  })
}

async function awaitIssueAndParent(
  issueMutation: Promise<ConfirmedIssue>,
  parentMutation?: Promise<ConfirmedParent>,
): Promise<ConfirmedIssue> {
  try {
    return await issueMutation
  } finally {
    try {
      await parentMutation
    } catch (error) {
      reportError(
        error instanceof Error ? error : new Error('Issue parent summary reconciliation failed', {cause: error}),
      )
    }
  }
}

export function useSetIssueStatus({
  issueId,
  parentIssueId,
  owner,
  repo,
  issueNumber,
}: {
  issueId: string
  parentIssueId?: string
  owner: string
  repo: string
  issueNumber: number
}): SetIssueStatus {
  const environment = useRelayEnvironment()
  const confirmedStateRef = useRef<IssueState | null>(null)
  const activeMutationCountRef = useRef(0)

  const {mutateAsync: mutateIssue} = useEntityMutation<ConfirmedIssue>({
    scope: {id: `issue-state:${owner}/${repo}#${issueNumber}`},
    mutationFn: operation => {
      const duplicateOf = operation.fields.duplicateOf as SetIssueStatusDuplicate | null
      return setIssueStatusRequest({
        owner,
        repo,
        issueNumber,
        issueId: operation.id,
        statusId: (operation.fields.status as IssueStatusOption).id,
        duplicateIssueId: duplicateOf?.id,
      })
    },
    getConfirmedOperation: (issue, operation) => {
      const selected = operation.fields.duplicateOf as SetIssueStatusDuplicate | null
      const duplicateOf = issue.duplicateOf && selected?.id === issue.duplicateOf.id ? selected : issue.duplicateOf
      return toOperation(issue.id, {
        state: issue.state,
        stateReason: issue.stateReason,
        closedAt: issue.closedAt,
        duplicateOf,
        status: issue.status,
      })
    },
  })
  const {mutateAsync: mutateParentSummary} = useParentSummaryMutation()

  return useCallback(
    (input: SetIssueStatusInput, callbacks?: SetIssueStatusCallbacks) => {
      const fields = optimisticFields(input)
      const optimisticDelta = input.currentState === fields.state ? 0 : fields.state === 'OPEN' ? 1 : -1

      if (activeMutationCountRef.current === 0) confirmedStateRef.current = input.currentState
      activeMutationCountRef.current += 1

      const relayBridge = environment.applyUpdate({
        storeUpdater: store => {
          writeStateToRelay(store, issueId, fields)
          if (parentIssueId && optimisticDelta !== 0) {
            adjustParentSummaryInRelay(store, parentIssueId, -optimisticDelta)
          }
          if (optimisticDelta !== 0) adjustOpenClosedCounts(store, optimisticDelta)
        },
      })

      const issueMutation = mutateIssue({operation: toOperation(issueId, fields)})
      const parentMutation = parentIssueId
        ? mutateParentSummary({
            operation: parentOperation(parentIssueId, -optimisticDelta),
            parentIssueId,
            result: issueMutation,
          })
        : undefined

      void (async () => {
        try {
          const issue = await awaitIssueAndParent(issueMutation, parentMutation)
          const previousConfirmedState = confirmedStateRef.current ?? input.currentState
          const committedDelta = previousConfirmedState === issue.state ? 0 : issue.state === 'OPEN' ? 1 : -1
          confirmedStateRef.current = issue.state

          // Commit the confirmed values before disposing the optimistic layer so the old state
          // doesn't flash between the two writes.
          try {
            commitLocalUpdate(environment, store => {
              writeStateToRelay(store, issue.id, issue)
              if (issue.parent) writeParentSummaryToRelay(store, issue.parent)
              if (committedDelta !== 0) adjustOpenClosedCounts(store, committedDelta)
            })
          } finally {
            relayBridge.dispose()
          }
          if (committedDelta > 0) incrementIssuesTabCounter()
          if (committedDelta < 0) decrementIssuesTabCounter()
          callbacks?.onCompleted?.(issue)
        } catch (error) {
          relayBridge.dispose()
          callbacks?.onError?.(error instanceof Error ? error : new Error(SET_STATUS_ERROR))
        } finally {
          activeMutationCountRef.current -= 1
        }
      })()
    },
    [environment, issueId, mutateIssue, mutateParentSummary, parentIssueId],
  )
}
